import * as React from "react";
import { useEffect, useMemo } from "react";

import { PopoverInteractionKind, Position } from "@blueprintjs/core";
import { ChevronDown, ChevronUp } from "lucide-react";

import { Popover } from "@/components/helpers/ui/blueprint-overrides/Popover";
import { cn } from "@/lib/utils";

import { Checkbox } from "./checkbox";
import { Input } from "./input";
import { Label } from "./label";
import { RadioGroup, RadioGroupItem } from "./radio-group";

/**
 * Filter types that determine whether selection is single (radio) or multiple (checkbox)
 */
export type FilterType = "single" | "multiple";

/**
 * Interface for filter items in single selection mode
 */
export interface SingleFilterOption {
  value: string | number | null;
  label: string;
  selectedLabel?: string;
}

/**
 * Interface for filter items in multiple selection mode
 */
export interface MultipleFilterOption {
  value: string | number | null;
  label: string;
  showDivider?: boolean;
  selectedLabel?: string;
}

export type FilterValue = string | number | null | undefined;
export type FilterValueArray = (string | number | null)[];
/**
 * Main filter component props
 */
export interface FilterProps {
  /** Type of filter: single for radio buttons, multiple for checkboxes */
  type?: FilterType;
  /** The currently selected value(s) */
  value?: FilterValue | FilterValueArray | boolean | null;
  /** Label displayed on the filter button */
  label: string;
  /** Label to show when items are selected (e.g. "Tags (2)") */
  selectedLabel?: string;
  /** Filter options to choose from */
  options: SingleFilterOption[] | MultipleFilterOption[];
  /** Optional CSS class name */
  className?: string;
  /** Button size variant */
  size?: "sm" | "lg";
  /** Callback when selection changes */
  onChange?: (value: FilterValue | FilterValueArray | boolean | null) => void;
  /** Whether to show a "View All" option (for multiple selection) */
  showViewAll?: boolean;
  /** Optional right icon to display on the filter button */
  rightIcon?: React.ReactNode;
  /** Optional left icon to display on the filter button */
  leftIcon?: React.ReactNode;
  /** Whether to show the dropdown icon */
  showDropdownIcon?: boolean;
  /** Whether to show the search input */
  showSearch?: boolean;
  /** Callback when the search input changes */
  onSearchChange?: (value: string) => void;
  /** Maximum height of the dropdown menu */
  maxHeight?: string;
  /** Initial value to set when the filter is mounted */
  defaultValue?: FilterValue | FilterValueArray;
  /** Callback when the view all option changes */
  onViewAllChange?: (isViewAll: boolean) => void;
  /** Callback when the filter is closed */
  onClose?: () => void;
}

const isArraysEqual = (a: FilterValueArray, b: FilterValueArray) => {
  if (a === null || b === null) {
    return false;
  }
  if (Array.isArray(a) && Array.isArray(b)) {
    if (a.length !== b.length) {
      return false;
    }
    const sortedA = [...a].sort();
    const sortedB = [...b].sort();
    return sortedA.every((value, index) => value === sortedB[index]);
  }
  return a === b;
};

export function Filter({
  type = "single",
  value,
  label,
  options,
  className,
  size = "sm",
  onChange,
  showViewAll = false,
  rightIcon,
  leftIcon,
  showDropdownIcon = true,
  showSearch = false,
  onSearchChange,
  defaultValue,
  onViewAllChange,
  onClose,
  maxHeight = "320px",
}: FilterProps) {
  // For single selection filters
  const [singleValue, setSingleValue] = React.useState<
    FilterValue | boolean | null
  >(type === "single" && !Array.isArray(value) ? value : undefined);
  const [isOpen, setIsOpen] = React.useState(false);

  // For multiple selection filters
  const [multipleValues, setMultipleValues] = React.useState<FilterValueArray>(
    type === "multiple" && Array.isArray(value) ? value : [],
  );

  useEffect(() => {
    if (type === "single" && !Array.isArray(value) && value !== singleValue) {
      setSingleValue(value);
    }
    if (
      type === "multiple" &&
      Array.isArray(value) &&
      !isArraysEqual(value, multipleValues)
    ) {
      setMultipleValues(value);
    }
  }, [value, type]);

  useEffect(() => {
    if (type === "single" && !Array.isArray(defaultValue) && defaultValue) {
      setSingleValue(defaultValue);
    }
  }, [defaultValue, type]);

  // Handle checkbox item selection
  const handleCheckboxToggle = (value: string | number | null) => {
    const newValues = multipleValues?.includes(value)
      ? multipleValues?.filter((v) => v !== value)
      : [...(multipleValues || []), value];

    setMultipleValues(newValues);
    onChange?.(newValues);
  };

  // Determine the button label based on selection
  const getButtonLabel = () => {
    if (type === "single") {
      if (defaultValue === singleValue) {
        return label;
      }
      const selectedOption = options.find(
        (option) => option.value === singleValue,
      );
      const selectedLabel =
        selectedOption?.selectedLabel || selectedOption?.label;

      return selectedLabel || label;
    }

    const isAllSelected =
      type === "multiple" && multipleValues?.length === options?.length;

    if (type === "multiple") {
      if (isAllSelected) {
        return label;
      } else if (multipleValues?.length && multipleValues?.length > 0) {
        return `${label} (${multipleValues?.length})`;
      } else {
        return label;
      }
    }

    return label;
  };

  useEffect(() => {
    onViewAllChange?.(multipleValues?.length === options?.length);
  }, [multipleValues, options, type, onViewAllChange]);

  const handleViewAll = () => {
    if (multipleValues?.length === options?.length) {
      setMultipleValues([]);
      onChange?.([]);
    } else {
      const allValues = options?.map((option) => option.value);
      setMultipleValues(allValues);
      onChange?.(allValues);
    }
  };

  const content = (
    <div className="mt-2">
      {type === "single" ? (
        <RadioGroup className="p-2">
          {options.map((option) => (
            <div
              key={option.value}
              className="flex cursor-pointer items-center space-x-2"
              onClick={() => {
                setSingleValue(option.value);
                onChange?.(option.value);
              }}
            >
              <RadioGroupItem
                key={option.value}
                checked={singleValue === option.value}
                value={option.value?.toString() || ""}
                id={option.value?.toString() || ""}
                className="size-5"
              />
              <Label
                htmlFor={option.value?.toString() || ""}
                className="cursor-pointer"
              >
                {option.label}
              </Label>
            </div>
          ))}
        </RadioGroup>
      ) : (
        <>
          {showViewAll && (
            <div className="flex flex-col">
              <div
                className="flex cursor-pointer items-center gap-2 bg-white p-2"
                onClick={handleViewAll}
              >
                <Checkbox
                  className="size-5"
                  checked={multipleValues?.length === options?.length}
                />
                <Label className="cursor-pointer">View All</Label>
              </div>
              <div className="h-[1px] w-full bg-gray-200" />
            </div>
          )}
          {showSearch && (
            <div className="flex items-center gap-2 p-2">
              <Input
                placeholder="Search"
                className="w-full"
                onChange={(e) => {
                  onSearchChange?.(e.target.value);
                }}
              />
            </div>
          )}

          {options?.map((option) => (
            <div
              key={option.value}
              className="flex cursor-pointer gap-2 p-2"
              onClick={() => {
                handleCheckboxToggle(option.value);
              }}
            >
              <Checkbox
                key={option.value}
                className="size-5"
                checked={multipleValues?.includes(option.value)}
              />
              <Label className="max-w-[300px] cursor-pointer leading-[1.5]">
                {option.label}
              </Label>
            </div>
          ))}
        </>
      )}
    </div>
  );

  const component = useMemo(() => {
    return (
      <Popover
        content={content}
        interactionKind={PopoverInteractionKind.CLICK}
        isOpen={isOpen}
        position={Position.BOTTOM_LEFT}
        minimal={true}
        onClose={() => {
          setIsOpen(false);
          onClose?.();
        }}
        onOpened={() => {
          setIsOpen(true);
        }}
        maxHeight={maxHeight}
      >
        <div
          className={cn(
            "inline-flex items-center justify-center cursor-pointer rounded-full whitespace-nowrap",
            "border text-sm font-semibold transition-colors focus-visible:outline-none",
            "h-8 gap-1.5 bg-white px-4 py-1.5",
            size === "lg" ? "h-auto rounded-lg px-3.5 py-3" : "",
            isOpen
              ? "border-blue-500 text-blue-500"
              : "border-[#E2E2E2] text-gray-700 hover:bg-gray-50",
            className,
          )}
        >
          {leftIcon && leftIcon}
          {getButtonLabel()}
          {rightIcon ||
            (showDropdownIcon && (isOpen ? <ChevronUp /> : <ChevronDown />))}
        </div>
      </Popover>
    );
  }, [
    getButtonLabel,
    isOpen,
    onClose,
    onChange,
    multipleValues,
    options,
    singleValue,
    type,
    maxHeight,
    content,
  ]);
  return component;
}

export interface FilterGroupProps {
  children: React.ReactNode;
  className?: string;
}

/**
 * Group multiple filters together with consistent spacing
 */
export function FilterGroup({ children, className }: FilterGroupProps) {
  return (
    <div
      className={cn("hide-scroll-bar flex gap-2 overflow-x-auto", className)}
    >
      {children}
    </div>
  );
}
